import React from 'react';
import { motion } from 'framer-motion';
import { FiShield, FiLock, FiEye, FiDatabase, FiGlobe, FiMail } from 'react-icons/fi';

const PrivacyPolicy = () => {
  const sections = [
    {
      id: 1,
      title: "Scope of This Policy",
      content: (
        <>
          <p className="text-gray-400 mb-4">This Policy applies to all users worldwide, including users from jurisdictions subject to:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>GDPR (European Union)</li>
            <li>PIPEDA (Canada)</li>
            <li>CCPA/CPRA (California)</li>
            <li>PDPA (Singapore)</li>
            <li>LGPD (Brazil)</li>
          </ul>
          <p className="text-gray-400 mt-4">We design our data practices to meet or exceed the strictest global privacy standards.</p>
        </>
      )
    },
    {
      id: 2,
      title: "Data We Collect",
      content: (
        <>
          <h4 className="text-lg font-semibold text-white mb-3">2.1 Account Information</h4>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>Full name</li>
            <li>Email address</li>
            <li>Username</li>
            <li>Phone number (optional)</li>
            <li>Country and region</li>
            <li>Authentication details</li>
          </ul>

          <h4 className="text-lg font-semibold text-white mb-3">2.2 Usage and Interaction Data</h4>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>Login activity</li>
            <li>Time spent on the platform</li>
            <li>AI prompts and interactions</li>
            <li>Trading journal entries</li>
            <li>Uploaded documents (optional)</li>
            <li>Feature usage analytics</li>
          </ul>

          <h4 className="text-lg font-semibold text-white mb-3">2.3 Device & Technical Data</h4>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>IP address</li>
            <li>Device identifiers</li>
            <li>Operating system</li>
            <li>Browser type</li>
            <li>Cookies and tracking technologies</li>
            <li>Log files</li>
          </ul>

          <h4 className="text-lg font-semibold text-white mb-3">2.4 Financial & Subscription Data</h4>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>Payment method (tokenized by Stripe/Paddle)</li>
            <li>Billing address</li>
            <li>Subscription type</li>
            <li>Transaction history</li>
          </ul>
          <p className="text-cyan-400 text-sm mb-4">We do not store full credit card numbers — these remain with PCI-compliant payment processors.</p>

          <h4 className="text-lg font-semibold text-white mb-3">2.5 Trading-Related Data (Optional)</h4>
          <p className="text-gray-400 mb-2">If you sync trading activity through integrations:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>Account statistics</li>
            <li>Trading history</li>
            <li>Broker/platform metadata</li>
            <li>Automated logs</li>
            <li>"No-Trade" behavior signals</li>
            <li>Risk profile data</li>
          </ul>
          <p className="text-gray-400 mb-4">This data powers our discipline engines and institutional-grade analysis systems.</p>

          <h4 className="text-lg font-semibold text-white mb-3">2.6 Sensitive Data</h4>
          <p className="text-gray-400">We do not intentionally collect government-issued IDs, health data, biometric identifiers, or minors' data.</p>
          <p className="text-cyan-400 text-sm mt-2">If provided voluntarily (e.g., user uploads), we protect it using strict access control.</p>
        </>
      )
    },
    {
      id: 3,
      title: "How We Use Personal Data",
      content: (
        <>
          <h4 className="text-lg font-semibold text-white mb-3">3.1 Provide & Improve the Service</h4>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>AI analysis</li>
            <li>Charting systems</li>
            <li>Pattern detection</li>
            <li>Behavioral risk engines</li>
            <li>Performance dashboards</li>
          </ul>

          <h4 className="text-lg font-semibold text-white mb-3">3.2 Enhance User Experience</h4>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>Personalization</li>
            <li>Adaptive UI</li>
            <li>Recommended features</li>
            <li>Customized AI models</li>
          </ul>

          <h4 className="text-lg font-semibold text-white mb-3">3.3 Ensure Security</h4>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>Fraud detection</li>
            <li>Access control</li>
            <li>Prevention of unauthorized use</li>
            <li>Monitoring compliance with Terms</li>
          </ul>

          <h4 className="text-lg font-semibold text-white mb-3">3.4 Marketing & Communication</h4>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>Product updates</li>
            <li>Educational content</li>
            <li>Special offers</li>
            <li>Notifications about new features</li>
          </ul>
          <p className="text-cyan-400 text-sm mb-4">Users may opt-out at any time.</p>

          <h4 className="text-lg font-semibold text-white mb-3">3.5 Legal & Regulatory Compliance</h4>
          <p className="text-gray-400 mb-2">We use data as required by:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4">
            <li>Anti-fraud regulations</li>
            <li>Anti-money laundering (AML) practices (if ever applicable)</li>
            <li>Corporate auditing</li>
            <li>Law enforcement requests (only when legally mandated)</li>
          </ul>
        </>
      )
    },
    {
      id: 4,
      title: "Legal Bases for Processing",
      content: (
        <>
          <p className="text-gray-400 mb-4">Depending on your jurisdiction, processing is based on:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>Consent</li>
            <li>Contract necessity</li>
            <li>Legitimate business interests</li>
            <li>Compliance with legal obligations</li>
            <li>Performance of core platform functions</li>
          </ul>
        </>
      )
    },
    {
      id: 5,
      title: "Data Sharing & Disclosure",
      content: (
        <>
          <p className="text-cyan-400 font-semibold mb-4">We do not sell personal data, including to advertisers.</p>
          <p className="text-gray-400 mb-4">We may share data with:</p>

          <h4 className="text-lg font-semibold text-white mb-3">5.1 Service Providers (Processors)</h4>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>Cloud hosting</li>
            <li>Analytics tools</li>
            <li>Email providers</li>
            <li>Payment processors</li>
          </ul>
          <p className="text-gray-400 mb-4">All vendors follow strict confidentiality agreements.</p>

          <h4 className="text-lg font-semibold text-white mb-3">5.2 Service Operator</h4>
          <ul className="list-disc list-inside text-gray-400 space-y-1 ml-4 mb-4">
            <li>SnapChart AI (IP ownership, governance, operations)</li>
          </ul>

          <h4 className="text-lg font-semibold text-white mb-3">5.3 Legal Requirements</h4>
          <p className="text-gray-400 mb-4">We may disclose information if required by law, regulation, subpoenas, or court orders — never voluntarily.</p>

          <h4 className="text-lg font-semibold text-white mb-3">5.4 Business Transactions</h4>
          <p className="text-gray-400">If the Company undergoes mergers, acquisitions, or financing rounds, anonymized or limited necessary personal data may be shared with investors or auditors under NDA.</p>
        </>
      )
    },
    {
      id: 6,
      title: "Data Retention",
      content: (
        <>
          <p className="text-gray-400 mb-4">We retain data only as long as necessary for:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>Account operation</li>
            <li>Legal obligations</li>
            <li>Security</li>
            <li>Fraud prevention</li>
            <li>Audit requirements</li>
          </ul>
          <p className="text-cyan-400 text-sm mt-4">Users may request deletion at any time (see Section 10).</p>
        </>
      )
    },
    {
      id: 7,
      title: "Data Protection & Security",
      content: (
        <>
          <p className="text-gray-400 mb-4">We apply enterprise-grade protection:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>AES-256 data encryption</li>
            <li>TLS 1.3 secure communication</li>
            <li>Role-based access control (RBAC)</li>
            <li>Multi-layer firewalls</li>
            <li>Continuous monitoring</li>
            <li>Zero-trust architecture</li>
            <li>Strict internal policies</li>
          </ul>
          <p className="text-cyan-400 text-sm mt-4">We follow the principles of "Privacy by Design".</p>
        </>
      )
    },
    {
      id: 8,
      title: "International Data Transfers",
      content: (
        <>
          <p className="text-gray-400 mb-4">Data may be processed in:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>Singapore</li>
            <li>United States</li>
            <li>Canada</li>
            <li>European Union</li>
            <li>Other cloud regions</li>
          </ul>
          <p className="text-gray-400 mt-4">Transfers comply with GDPR-approved mechanisms (SCCs), PDPA, and global privacy frameworks.</p>
        </>
      )
    },
    {
      id: 9,
      title: "Cookies & Tracking Technologies",
      content: (
        <>
          <p className="text-gray-400 mb-4">We use:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>Essential cookies</li>
            <li>Analytics cookies</li>
            <li>Session tracking</li>
            <li>Error logs</li>
            <li>Optional personalization cookies</li>
          </ul>
          <p className="text-cyan-400 text-sm mt-4">Users may decline non-essential cookies.</p>
        </>
      )
    },
    {
      id: 10,
      title: "Your Rights",
      content: (
        <>
          <p className="text-gray-400 mb-4">Depending on your jurisdiction, you may:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>Access your data</li>
            <li>Correct inaccurate data</li>
            <li>Delete your data ("Right to Erasure")</li>
            <li>Export your data (portability)</li>
            <li>Restrict processing</li>
            <li>Withdraw consent</li>
            <li>Object to marketing</li>
            <li>Request copies of stored data</li>
          </ul>
          <div className="mt-6 p-4 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-xl">
            <p className="text-gray-300">To exercise rights, contact:</p>
            <a href="mailto:privacy@snap-chart.ai" className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium">
              privacy@snap-chart.ai
            </a>
          </div>
        </>
      )
    },
    {
      id: 11,
      title: "Children's Privacy",
      content: (
        <>
          <p className="text-gray-400">Our Service is not intended for individuals under 18 years old.</p>
          <p className="text-cyan-400 text-sm mt-2">We do not knowingly collect data from minors.</p>
        </>
      )
    },
    {
      id: 12,
      title: "Changes to This Policy",
      content: (
        <>
          <p className="text-gray-400 mb-2">We may update this Policy periodically.</p>
          <p className="text-gray-400 mb-2">When updated, the "Last Updated" date will change.</p>
          <p className="text-gray-400">Continued use of the Service indicates acceptance of updated terms.</p>
        </>
      )
    },
    {
      id: 13,
      title: "Contact Information",
      content: (
        <>
          <p className="text-gray-400 mb-4">For privacy concerns, requests, or questions:</p>
          <div className="p-4 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-xl">
            <h4 className="text-white font-semibold mb-3">SnapChart AI</h4>
            <div className="space-y-1.5">
              <a href="mailto:privacy@snap-chart.ai" className="text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-2">
                <FiMail className="text-sm" />
                privacy@snap-chart.ai
              </a>
              <a href="mailto:support@snap-chart.ai" className="text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-2">
                <FiMail className="text-sm" />
                support@snap-chart.ai
              </a>
            </div>
          </div>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-cyan-600/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-r from-purple-600/20 to-cyan-600/20 blur-3xl rounded-full"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                <FiShield className="text-3xl text-cyan-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                PRIVACY POLICY
              </span>
            </h1>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-gray-400 text-sm">
              <span>Effective Date: December 6, 2025</span>
              <span className="hidden sm:block">•</span>
              <span>Last Updated: December 6, 2025</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Introduction */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-2xl"
        >
          <p className="text-gray-300 leading-relaxed">
            This Privacy Policy ("Policy") explains how <span className="text-cyan-400 font-medium">SnapChart AI</span> ("we", "us", "our", or "the Service") collects, uses, discloses, and protects personal data when you access or use SnapChart AI, TitanVue AI, or any related products or services (the "Service").
          </p>
          <p className="text-gray-400 mt-4">
            By using the Service, you agree to the practices described in this Policy.
          </p>
        </motion.div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="p-6 bg-gray-800/30 border border-white/5 rounded-2xl hover:border-purple-500/20 transition-colors"
            >
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 text-sm font-bold">
                  {section.id}
                </span>
                <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  {section.title}
                </span>
              </h3>
              <div className="pl-11">
                {section.content}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
